import React, { useContext, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "react-toastify";
import FinanceV2Layout from "./FinanceV2Layout";
import { NoteState } from "../../context/notes/NoteState";
import FinanceV2Services from "../../UserServices/FinanceV2Services";
import { downloadCsv } from "./financeV2Export";
import "./FinanceV2.css";

export default function FinanceV2GeneralLedger() {
  const { state } = useContext(NoteState);
  const { branch_id } = state;
  const [accounts, setAccounts] = useState([]);
  const [accountId, setAccountId] = useState("");
  const mtd = (() => {
    const now = new Date();
    return {
      from: new Date(now.getFullYear(), now.getMonth(), 1).toISOString().split("T")[0],
      to: now.toISOString().split("T")[0],
    };
  })();
  const [from, setFrom] = useState(mtd.from);
  const [to, setTo] = useState(mtd.to);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    FinanceV2Services.getAccountsList(branch_id)
      .then((res) => setAccounts(res.data?.data || []))
      .catch(() => toast.error("Could not load accounts"));
  }, [branch_id]);

  const load = async () => {
    if (!accountId) {
      toast.info("Select an account");
      return;
    }
    setLoading(true);
    try {
      const res = await FinanceV2Services.getGeneralLedger(accountId, {
        from,
        to,
        branch_id,
      });
      setRows(res.data?.data || []);
    } catch (e) {
      toast.error("Could not load general ledger");
      setRows([]);
    } finally {
      setLoading(false);
    }
  };

  const selected = accounts.find((a) => String(a.id) === String(accountId));

  return (
    <FinanceV2Layout>
      <div className="container-fluid px-0 py-2">
        <div className="d-flex justify-content-between align-items-center mb-3">
          <h4 className="mb-0">General ledger</h4>
          <Link to="/finance-v2/dashboard" className="btn btn-sm btn-outline-secondary">
            Dashboard
          </Link>
        </div>

        <div className="fv2-card mb-3">
          <div className="row g-2 align-items-end">
            <div className="col-md-5">
              <label className="form-label small">Account</label>
              <select
                className="form-select form-select-sm"
                value={accountId}
                onChange={(e) => setAccountId(e.target.value)}
              >
                <option value="">— Select —</option>
                {accounts.map((a) => (
                  <option key={a.id} value={a.id}>
                    {a.code} — {a.name}
                  </option>
                ))}
              </select>
            </div>
            <div className="col-md-2">
              <label className="form-label small">From</label>
              <input
                type="date"
                className="form-control form-control-sm"
                value={from}
                onChange={(e) => setFrom(e.target.value)}
              />
            </div>
            <div className="col-md-2">
              <label className="form-label small">To</label>
              <input
                type="date"
                className="form-control form-control-sm"
                value={to}
                onChange={(e) => setTo(e.target.value)}
              />
            </div>
            <div className="col-md-3 d-flex gap-1 flex-wrap">
              <button type="button" className="btn btn-sm btn-success" disabled={loading} onClick={load}>
                {loading ? "Loading…" : "Run report"}
              </button>
              <button
                type="button"
                className="btn btn-sm btn-outline-dark"
                disabled={!rows.length}
                onClick={() =>
                  downloadCsv(
                    `gl-${selected?.code || accountId}-${from}-${to}.csv`,
                    ["Date", "Reference", "Description", "Debit", "Credit", "Balance"],
                    rows.map((r) => [
                      r.entry_date,
                      r.reference,
                      r.description,
                      r.debit,
                      r.credit,
                      r.running_balance,
                    ])
                  )
                }
              >
                CSV
              </button>
            </div>
          </div>
          {selected && (
            <p className="text-muted small mt-2 mb-0">
              {selected.account_type?.name} · Normal balance: {selected.account_type?.normal_balance}
            </p>
          )}
        </div>

        <div className="fv2-table-wrap">
          <table className="table table-sm table-striped">
            <thead>
              <tr>
                <th>Date</th>
                <th>Reference</th>
                <th>Description</th>
                <th className="text-end">Debit</th>
                <th className="text-end">Credit</th>
                <th className="text-end">Balance</th>
              </tr>
            </thead>
            <tbody>
              {rows.length === 0 ? (
                <tr>
                  <td colSpan={6} className="text-muted text-center">
                    {loading ? "Loading…" : "No lines — select account and run report"}
                  </td>
                </tr>
              ) : (
                rows.map((r, i) => (
                  <tr key={i}>
                    <td>{r.entry_date}</td>
                    <td>{r.reference}</td>
                    <td>{r.description}</td>
                    <td className="text-end">{r.debit}</td>
                    <td className="text-end">{r.credit}</td>
                    <td className="text-end fw-semibold">{r.running_balance}</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </FinanceV2Layout>
  );
}
